import { BoxPaddingType, ResponsiveTextPadding } from "@/types/LayoutType";

/**
 * Cleans CSS values coming from the backend:
 * - trims leading/trailing whitespace
 * - collapses multiple spaces into a single space
 * - keeps expressions like "calc(...)" intact
 */
export const cleanCssValue = (v: unknown): string => {
  if (v == null) return "";
  return String(v).trim().replace(/\s+/g, " ");
};

type ResponsiveRecord<K extends string, V> = Partial<Record<K, V>>;

type GetResponsiveCssVarsOptions<K extends string, V> = {
  /**
   * Base CSS variable name without breakpoint suffix.
   * Example: "--box-padding" -> "--box-padding-mobile"
   */
  varName: `--${string}`;

  /** Responsive values per breakpoint */
  values: ResponsiveRecord<K, V>;

  /** Optional fallback values per breakpoint */
  defaults?: ResponsiveRecord<K, V>;

  /**
   * Optional explicit breakpoint order.
   * If not provided, keys from values + defaults will be used.
   */
  breakpoints?: readonly K[];

  /**
   * Optional value transformer.
   * Useful for unit conversion or value formatting.
   */
  transform?: (value: V, breakpoint: K) => string;
};

/**
 * Generic generator for responsive CSS variables.
 *
 * Example:
 * { mobile: "1rem" }
 * ->
 * { "--x-mobile": "1rem" }
 */
export const getResponsiveCssVars = <K extends string, V>(
  opts: GetResponsiveCssVarsOptions<K, V>,
): Record<string, string> => {
  const {
    varName,
    values,
    defaults,
    breakpoints,
    transform = (value) => cleanCssValue(value),
  } = opts;

  const bps =
    breakpoints ??
    ([
      ...new Set([
        ...Object.keys(defaults ?? {}),
        ...Object.keys(values ?? {}),
      ]),
    ] as K[]);

  const out: Record<string, string> = {};

  for (const bp of bps) {
    const value = (values?.[bp] ?? defaults?.[bp]) as V | undefined;
    if (value == null) continue;

    out[`${varName}-${bp}`] = transform(value, bp);
  }

  return out;
};

/* -------------------------------------------------------------------------------------------------
 * Box padding vars
 * ------------------------------------------------------------------------------------------------- */

const BOX_PADDING_BREAKPOINTS = [
  "mobile",
  "tablet",
  "desktop",
  "fullhd",
] as const;
type BoxPaddingBreakpoint = (typeof BOX_PADDING_BREAKPOINTS)[number];

/**
 * Specific helper for box padding CSS variables.
 *
 * Generates:
 * --box-padding-mobile
 * --box-padding-tablet
 * --box-padding-desktop
 * --box-padding-fullhd
 */
export const getBoxPaddingVars = (
  boxPadding: Partial<BoxPaddingType> | undefined | null,
  defaults: BoxPaddingType = {
    mobile: "1.5rem",
    tablet: "2rem",
    desktop: "2.5rem",
    fullhd: "3rem",
  },
): Record<string, string> => {
  return getResponsiveCssVars<BoxPaddingBreakpoint, string>({
    varName: "--box-padding",
    values: (boxPadding ?? {}) as Partial<BoxPaddingType>,
    defaults,
    breakpoints: BOX_PADDING_BREAKPOINTS, // always the same 4 keys
    transform: (v) => cleanCssValue(v),
  });
};

/* -------------------------------------------------------------------------------------------------
 * Split/content text padding vars (top_bottom / left_right)
 * ------------------------------------------------------------------------------------------------- */

/**
 * We only get 4 breakpoints from the backend (mobile/tablet/desktop/fullhd),
 * but Split supports 6 (mobile/tablet/desktop/widescreen/fullhd/ultrahd).
 *
 * Rules:
 * - widescreen copies the last available from desktop/tablet/mobile
 * - ultrahd copies fullhd (or last available if fullhd is missing)
 */
const SPLIT_TEXT_PADDING_BREAKPOINTS = [
  "mobile",
  "tablet",
  "desktop",
  "widescreen",
  "fullhd",
  "ultrahd",
] as const;

type SplitTextPaddingBreakpoint =
  (typeof SPLIT_TEXT_PADDING_BREAKPOINTS)[number];

type XY = { top_bottom: string; left_right: string };

/**
 * Generates Split content padding vars from ResponsiveTextPadding.
 *
 * Output:
 * --split-content-padding-x-mobile  (left_right)
 * --split-content-padding-y-mobile  (top_bottom)
 * ...and so on incl. widescreen/ultrahd (filled via "last available").
 */
export const getSplitContentPaddingVars = (
  textPadding: Partial<ResponsiveTextPadding> | undefined | null,
  defaults: ResponsiveTextPadding = {
    mobile: { top_bottom: "0px", left_right: "0px" },
    tablet: { top_bottom: "0px", left_right: "0px" },
    desktop: { top_bottom: "0px", left_right: "0px" },
    fullhd: { top_bottom: "0px", left_right: "0px" },
  },
): Record<string, string> => {
  const values = textPadding ?? {};

  const resolveXY = (bp: keyof ResponsiveTextPadding): XY => ({
    left_right: values[bp]?.left_right ?? defaults[bp].left_right,
    top_bottom: values[bp]?.top_bottom ?? defaults[bp].top_bottom,
  });

  // Resolve the 4 backend breakpoints first (always defined because defaults exist)
  const resolved: Record<keyof ResponsiveTextPadding, XY> = {
    mobile: resolveXY("mobile"),
    tablet: resolveXY("tablet"),
    desktop: resolveXY("desktop"),
    fullhd: resolveXY("fullhd"),
  };

  // "Last available" among mobile -> tablet -> desktop
  const lastUpToDesktop: XY =
    resolved.desktop?.left_right || resolved.desktop?.top_bottom
      ? resolved.desktop
      : resolved.tablet?.left_right || resolved.tablet?.top_bottom
        ? resolved.tablet
        : resolved.mobile;

  // widescreen: last available before it (desktop/tablet/mobile)
  const widescreen: XY = lastUpToDesktop;

  // ultrahd: copy fullhd (or last available if fullhd is effectively empty)
  const ultrahd: XY =
    resolved.fullhd?.left_right || resolved.fullhd?.top_bottom
      ? resolved.fullhd
      : lastUpToDesktop;

  const splitResolved: Record<SplitTextPaddingBreakpoint, XY> = {
    mobile: resolved.mobile,
    tablet: resolved.tablet,
    desktop: resolved.desktop,
    widescreen,
    fullhd: resolved.fullhd,
    ultrahd,
  };

  const xDefaults: Record<SplitTextPaddingBreakpoint, string> = {
    mobile: splitResolved.mobile.left_right,
    tablet: splitResolved.tablet.left_right,
    desktop: splitResolved.desktop.left_right,
    widescreen: splitResolved.widescreen.left_right,
    fullhd: splitResolved.fullhd.left_right,
    ultrahd: splitResolved.ultrahd.left_right,
  };

  const yDefaults: Record<SplitTextPaddingBreakpoint, string> = {
    mobile: splitResolved.mobile.top_bottom,
    tablet: splitResolved.tablet.top_bottom,
    desktop: splitResolved.desktop.top_bottom,
    widescreen: splitResolved.widescreen.top_bottom,
    fullhd: splitResolved.fullhd.top_bottom,
    ultrahd: splitResolved.ultrahd.top_bottom,
  };

  // We emit all 6 breakpoints by putting them into defaults (values can stay empty)
  return {
    ...getResponsiveCssVars<SplitTextPaddingBreakpoint, string>({
      varName: "--split-content-padding-x",
      values: {},
      defaults: xDefaults,
      breakpoints: SPLIT_TEXT_PADDING_BREAKPOINTS,
      transform: (v) => cleanCssValue(v),
    }),
    ...getResponsiveCssVars<SplitTextPaddingBreakpoint, string>({
      varName: "--split-content-padding-y",
      values: {},
      defaults: yDefaults,
      breakpoints: SPLIT_TEXT_PADDING_BREAKPOINTS,
      transform: (v) => cleanCssValue(v),
    }),
  };
};
